import {AgentApprovalResponse, AgentType, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatFileUpload} from 'llm/chat-library/ChatLibraryTypes';
import type {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {assertNever} from 'util/Types';

import {
  ClientChatEvent,
  ClientDisplayChatEvent,
  ClientOptimisticUserMessage,
  ClientProgressMessage,
  ClientStreamingAssistantMessage,
  DisplayMessageAssistantResponseType,
  DisplayMessageChatEventNode,
  ProgressStatus,
} from './MessageTypes';

export function filterDisplayEvents(events: Array<ClientChatEvent>): Array<ClientDisplayChatEvent> {
  return events.filter(
    (event): event is ClientDisplayChatEvent =>
      event.__typename === 'DisplayMessageChatEventNode' ||
      event.__typename === 'ClientOptimisticUserMessage' ||
      event.__typename === 'ClientProgressMessage' ||
      event.__typename === 'ClientStreamingAssistantMessage'
  );
}

/** Short user-facing label for save flows and filenames ("Message" vs "Report"). */
export function getMessageResponseTypeAsText(
  responseType: DisplayMessageAssistantResponseType
): 'Message' | 'Report' {
  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPLY:
      return 'Message';
    case DisplayMessageAssistantResponseType.REPORT:
      return 'Report';
    default:
      assertNever(responseType);
      return 'Message';
  }
}

export function getDisplayMessageAssistantResponseType(
  event: DisplayMessageChatEventNode | ClientStreamingAssistantMessage
): DisplayMessageAssistantResponseType {
  // Prefer the persisted displayType from message blocks when available.
  const hasReportBlock = event.messageBlocks.some(
    (block) => block.__typename === 'DisplayTextBlockNode' && block.displayType === 'report'
  );
  if (hasReportBlock) {
    return DisplayMessageAssistantResponseType.REPORT;
  }

  // Fall back to agent type for backward compatibility (existing messages without displayType).
  switch (event.agentType) {
    case AgentType.DEEP_RESEARCH:
      return DisplayMessageAssistantResponseType.REPORT;
    case AgentType.ASK:
    case AgentType.DATA_CAPTURE:
    case AgentType.LEGACY_DATA_IMPORT:
    case AgentType.ANALYSIS:
    case AgentType.CO_SCIENTIST:
    case AgentType.ASSISTANT:
    case AgentType.ONBOARDING:
    case AgentType.PIPELINES_CUSTOM_CODE_WRITER:
      return DisplayMessageAssistantResponseType.REPLY;
    default:
      assertNever(event.agentType);
      return DisplayMessageAssistantResponseType.REPLY;
  }
}

/**
 * Whether an assistant display message is a write-approval request (it carries a non-null
 * ``agentApprovalRequest``). Such a message renders like a normal assistant message but gets a
 * distinct approval (Allow/Deny) system card appended at the bottom.
 */
export function isApprovalRequestMessage(
  event: DisplayMessageChatEventNode
): event is DisplayMessageChatEventNode & {
  agentApprovalRequest: NonNullable<DisplayMessageChatEventNode['agentApprovalRequest']>;
} {
  return event.agentApprovalRequest != null;
}

/**
 * The user's approval response answering the request identified by ``requestEventId``, if a decision
 * has persisted.
 *
 * Scans ``events`` from newest to oldest for a user display message whose
 * ``agentApprovalDecision`` references that request (its ``requestEvent.id`` matches
 * ``requestEventId``) and returns that decision's response (APPROVED_THIS_CHAT /
 * EXPLICITLY_DENIED / IMPLICITLY_DENIED). Returns ``null`` when no persisted decision answers the
 * request yet — e.g. it is still awaiting a response, or the reply is only optimistic. An answered
 * request shows a static Approval granted/Approval not granted indicator instead of the actionable
 * Allow/Deny buttons, so stale requests in history can't be re-answered.
 */
export function getAnsweredApprovalStatus(
  events: Array<ClientDisplayChatEvent>,
  requestEventId: string
): AgentApprovalResponse | null {
  for (let index = events.length - 1; index >= 0; index--) {
    const event = events[index];
    if (event.__typename !== 'DisplayMessageChatEventNode') {
      continue;
    }
    const decision = event.agentApprovalDecision;
    if (decision != null && decision.requestEvent.id === requestEventId) {
      return decision.response;
    }
  }
  return null;
}

export function createProgressMessage({
  status,
  errorMessage = null,
  agentType = null,
  progressData = null,
  associatedMessageId,
  thinkingDefaultExpanded,
}: {
  status: ProgressStatus;
  errorMessage?: string | null;
  agentType?: AgentType | null;
  progressData?: ProgressData | null;
  associatedMessageId?: string;
  thinkingDefaultExpanded?: boolean;
}): ClientProgressMessage {
  const message: ClientProgressMessage = {
    __typename: 'ClientProgressMessage',
    status,
    errorMessage,
    agentType,
    role: null,
    progressData,
  };
  // Only include associatedMessageId if provided (for completed thinking sections)
  if (associatedMessageId !== undefined) {
    message.associatedMessageId = associatedMessageId;
  }
  if (thinkingDefaultExpanded !== undefined) {
    message.thinkingDefaultExpanded = thinkingDefaultExpanded;
  }
  return message;
}

export function createStreamingMessage({
  createdAt,
  agentType,
  responseToEventId,
  streamingContent,
  displayType = null,
  progressMessage = null,
}: {
  createdAt: string;
  agentType: AgentType;
  responseToEventId: string;
  streamingContent: string;
  displayType?: string | null;
  progressMessage?: string | null;
}): ClientStreamingAssistantMessage {
  return {
    __typename: 'ClientStreamingAssistantMessage',
    responseToEventId,
    messageBlocks: [
      {
        __typename: 'DisplayTextBlockNode',
        text: streamingContent,
        displayType,
      },
    ],
    role: LLMRole.ASSISTANT,
    agentType,
    createdAt,
    progressMessage,
  };
}

export function createPendingUserMessage({
  text,
  fileUploads,
  agentType,
  replyToId,
  retryOfId,
}: {
  text: string;
  fileUploads: Array<ChatFileUpload>;
  agentType: AgentType;
  /** The ID of the last display message (user or assistant) at the time this optimistic message was sent. */
  replyToId: string | null;
  /** If this is a retry, the ID of the original user message being retried. */
  retryOfId: string | null;
}): ClientOptimisticUserMessage {
  return {
    __typename: 'ClientOptimisticUserMessage',
    role: LLMRole.USER,
    agentType,
    messageBlocks: [
      {
        __typename: 'DisplayTextBlockNode',
        text,
        displayType: null,
      },
      ...fileUploads.map((fileUpload) => ({
        __typename: 'DisplayFileBlockNode' as const,
        filename: fileUpload.filename,
        mediaType: fileUpload.mediaType,
      })),
    ],
    replyToId,
    retryOfId,
  };
}
